import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { washPhotoPhaseSchema, washReadySmsStatusSchema, washWorkOrderStatusSchema } from "./enums.js";
/**
 * El yıkama iş emri (Prompt 18, ADR 0018 §13). Numara boşluksuz sequence'tir (`YKM2026000012`). Durum makinesi
 * `WAITING → IN_PROGRESS → READY → DELIVERED` (+ `CANCELLED` yalnızca WAITING / IN_PROGRESS'ten); geçersiz geçiş `409
 * INVALID_STATE_TRANSITION`. Hizmet satırının birim fiyatı programın satış ürününün O ANKİ istasyon fiyatının anlık
 * görüntüsüdür (KDV dahil, kuruş); toplam = Σ satır.
 *
 * READY'de telefon varsa işlemsel "aracınız hazır" SMS'i gider (sonuç `readySmsStatus`; hata geçişi ENGELLEMEZ). DELIVERED
 * bağlı bir kasa satışı (`PosSale.washWorkOrderNo`) ya da ödemesiz teslim gerekçesi (`unpaidReason`) ister — yoksa `422
 * WASH_PAYMENT_REQUIRED`; teslimde satır adedi kadar `MANUAL` makine çalışması (döngü) yazılır.
 *
 * KVKK: plaka, ad, telefon ve not kişisel veridir; `wash.operate` (o istasyonda) ya da `customers.personal.view` yoksa MASKELİ.
 */
const personal = (description) => z.string().nullable().describe(`KVKK: ${description} (maskeli olabilir)`);
export const WASH_PAYMENT_WARNINGS = ["AMOUNT_MISMATCH", "SALE_VOIDED", "PAID_CANCELLED_ORDER"];
export const washPaymentWarningSchema = z.enum(WASH_PAYMENT_WARNINGS);
export const WASH_PAYMENT_WARNING_LABELS = {
    AMOUNT_MISMATCH: "Kasada ödenen yıkama tutarı iş emri toplamından farklı",
    SALE_VOIDED: "Bağlı kasa satışı iptal edildi",
    PAID_CANCELLED_ORDER: "İptal edilmiş iş emrine kasada ödeme alındı",
};
export const washWorkOrderLineSchema = z.object({
    lineNo: z.number().int(),
    programId: uuidSchema,
    programCode: z.string(),
    programName: z.string(),
    productId: uuidSchema,
    quantity: z.number().int(),
    unitPrice: moneySchema.describe("KDV dahil birim fiyat (kuruş) — istasyon fiyatının anlık görüntüsü"),
    amount: moneySchema,
    durationSeconds: z.number().int().nullable(),
});
export const washWorkOrderPhotoSchema = z.object({
    id: uuidSchema,
    phase: washPhotoPhaseSchema,
    fileId: uuidSchema,
    caption: z.string().nullable(),
    mimeType: z.string(),
    createdAt: timestampSchema,
    purgedAt: timestampSchema
        .nullable()
        .describe("Dosya imha edildi (saklama süresi / kaldırma / KVKK) — kayıt kalır"),
    downloadUrl: z.string().nullable().describe("Kısa ömürlü indirme adresi; imha edilmişse null"),
    downloadExpiresAt: timestampSchema.nullable(),
});
export const washWorkOrderSummarySchema = z.object({
    id: uuidSchema,
    orderNo: z.string(),
    companyId: uuidSchema,
    stationId: uuidSchema,
    unitId: uuidSchema.nullable(),
    unitName: z.string().nullable(),
    status: washWorkOrderStatusSchema,
    plate: personal("araç plakası (normalleştirilmiş)"),
    customerProfileId: uuidSchema.nullable(),
    contactName: personal("ad soyad"),
    programNames: z.array(z.string()),
    totalAmount: moneySchema,
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable(),
    appointmentId: uuidSchema.nullable(),
    posSaleId: uuidSchema.nullable().describe("Ödemenin kasa satışı (bağı worker kurar)"),
    paidAmount: moneySchema.nullable().describe("Bağlı satıştaki yıkama satırları (iadeler düşülmüş)"),
    paymentWarning: washPaymentWarningSchema.nullable(),
    readySmsStatus: washReadySmsStatusSchema,
    waitingAt: timestampSchema,
    inProgressAt: timestampSchema.nullable(),
    readyAt: timestampSchema.nullable(),
    deliveredAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    estimatedReadyAt: timestampSchema
        .nullable()
        .describe("İşlemde: başlangıç + hizmet süreleri; hazırda hazır olma anı; beklemede null"),
    allowedTransitions: z
        .array(washWorkOrderStatusSchema)
        .describe("Durum makinesinin (geçiş tablosu) bu durumdan izin verdiği hedefler"),
    requiresUnpaidReason: z
        .boolean()
        .describe("Teslim (DELIVERED) izinli ama geçerli kasa ödemesi yok (bağlı satış yok ya da tamamen iptal) → teslim ödemesiz teslim gerekçesi ister"),
    unpaidDeliveryReason: z.string().nullable().describe("Ödemesiz teslim gerekçesi (teslim edildiyse)"),
    personalDataMasked: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const washWorkOrderPageSchema = pageOf(washWorkOrderSummarySchema);
export const washWorkOrderSchema = washWorkOrderSummarySchema.extend({
    stationName: z.string(),
    contactPhone: personal("telefon (E.164)"),
    note: personal("not"),
    lines: z.array(washWorkOrderLineSchema),
    photos: z.array(washWorkOrderPhotoSchema),
    appointmentStartsAt: timestampSchema.nullable(),
    paymentWarningMessage: z.string().nullable(),
    cancelReason: z.string().nullable(),
    readySmsAt: timestampSchema.nullable(),
    personalDataErasedAt: timestampSchema.nullable(),
});
export const listWashWorkOrdersQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    status: washWorkOrderStatusSchema.optional(),
    unitId: uuidSchema.optional(),
    from: localDateSchema.optional().describe("Açılış günü (İstanbul, dahil)"),
    to: localDateSchema.optional().describe("Açılış günü (İstanbul, dahil)"),
    deliveredFrom: localDateSchema
        .optional()
        .describe("Teslim günü (İstanbul, dahil) — verilirse yalnızca teslim edilmiş iş emirleri"),
    deliveredTo: localDateSchema.optional().describe("Teslim günü (İstanbul, dahil)"),
});
export const washWorkOrderLineInputSchema = z.object({
    programId: uuidSchema.describe("HAND_WASH programı (ünite verildiyse o ünitede tanımlı)"),
    quantity: z.number().int().min(1).max(20).default(1),
});
const linesSchema = z
    .array(washWorkOrderLineInputSchema)
    .min(1)
    .max(20)
    .refine((lines) => new Set(lines.map((line) => line.programId)).size === lines.length, "Aynı hizmet iki satırda yer alamaz (adedi artırın)");
const workOrderFields = {
    unitId: uuidSchema.nullable().default(null).describe("El yıkama (HAND_WASH) ünitesi"),
    plate: z.string().trim().min(2).max(20).describe("KVKK: araç plakası"),
    customerProfileId: uuidSchema.nullable().default(null),
    contactName: z.string().trim().min(2).max(120).nullable().default(null).describe("KVKK: ad soyad"),
    contactPhone: z
        .string()
        .trim()
        .min(7)
        .max(30)
        .nullable()
        .default(null)
        .describe("KVKK: telefon (E.164'e çevrilir) — hazır SMS'i buraya gider"),
    note: z.string().trim().max(1000).nullable().default(null),
    staffId: uuidSchema.nullable().default(null).describe("İstasyon personeli (`station_staff`)"),
    lines: linesSchema,
};
export const createWashWorkOrderSchema = z.object({ stationId: uuidSchema, ...workOrderFields });
/** Yalnızca WAITING / IN_PROGRESS iş emrinde. Satırlar verilirse TAMAMI yeniden yazılır (var olan hizmetin fiyatı korunur). */
export const updateWashWorkOrderSchema = patchOf(z.object(workOrderFields)).extend({
    version: z.number().int().min(1),
});
export const transitionWashWorkOrderSchema = z
    .object({
    to: washWorkOrderStatusSchema,
    reason: z.string().trim().min(3).max(500).optional().describe("İptal gerekçesi (CANCELLED'de zorunlu)"),
    unpaidReason: z
        .string()
        .trim()
        .min(3)
        .max(500)
        .optional()
        .describe("Ödemesiz teslim gerekçesi (abonelik / garanti / ikram) — bağlı kasa satışı yoksa zorunlu"),
    version: z.number().int().min(1).optional(),
})
    .superRefine((value, context) => {
    if (value.to === "CANCELLED" && !value.reason)
        context.addIssue({ code: "custom", path: ["reason"], message: "İptal gerekçesi zorunludur" });
});
export const WASH_PHOTOS_PER_PHASE = 12;
export const attachWashWorkOrderPhotoSchema = z.object({
    fileId: uuidSchema.describe("Yüklenmiş (UPLOADED), amacı WASH_PHOTO olan dosya"),
    phase: washPhotoPhaseSchema,
    caption: z.string().trim().max(200).nullable().default(null),
});
export const washWorkOrderPhotoParamsSchema = z.object({ id: uuidSchema, photoId: uuidSchema });
//# sourceMappingURL=work-orders.js.map